import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { Footer } from '../components/Footer.js';
import type { MeUser } from '../../shared/protocol.js';
import { INVITE_CODE_LENGTH, normalizeInviteCode } from '../../shared/registration.js';

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];

/**
 * Trying the arena as a guest with a one-time guest code (decision 2026-09-29). From a QR code the
 * code is filled in and cannot be changed. The visit starts right away with a generated name.
 */
export function GuestView(props: { param: string | null; onLogin: (user: MeUser) => void }) {
  const locked = props.param !== null;
  const [code, setCode] = useState(props.param ? normalizeInviteCode(props.param) : '');
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checked, setChecked] = useState(!locked);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!props.param) return;
    api<{ code: string; expiresAt: number; arenaClosed: boolean }>('POST', '/api/join/guest/check', { code: props.param })
      .then((r) => (r.arenaClosed ? setCheckError(t.errors.arena_closed) : setChecked(true)))
      .catch((err) => setCheckError(errorText(err)));
  }, [props.param]);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ user: MeUser }>('POST', '/api/join/guest', { code: normalizeInviteCode(code) });
      props.onLogin(r.user);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const g = t.guest;
  return (
    <div class="center page-with-footer">
      <form class="card login join" onSubmit={submit}>
        <h1 class="login-logo">
          <Logo size="large" animated />
        </h1>
        <h2 class="center-text">{g.title}</h2>
        {checkError ? (
          <p class="error" role="alert">
            {checkError}
          </p>
        ) : !checked ? (
          <p class="subtitle" aria-busy="true">
            {t.join.checking}
          </p>
        ) : (
          <>
            <p class="subtitle">{g.intro}</p>
            <ul class="guest-terms">
              {g.terms.map((term) => (
                <li key={term}>{term}</li>
              ))}
            </ul>
            <label>
              {g.code}
              <input
                class="code-input"
                value={code}
                onInput={(e) => setCode(e.currentTarget.value.toUpperCase())}
                readOnly={locked}
                maxLength={INVITE_CODE_LENGTH + 2}
                autocomplete="off"
                autocapitalize="characters"
                spellcheck={false}
                required
                autofocus={!locked}
              />
              {!locked && <small>{t.join.codeHint}</small>}
            </label>
            {error && (
              <p class="error" role="alert">
                {error}
              </p>
            )}
            <button class="btn btn-primary" type="submit" disabled={busy} autofocus={locked}>
              {busy ? g.starting : g.start}
            </button>
            <p class="hint center-text">
              <a href="tietosuoja.html">{t.join.privacy}</a>
            </p>
          </>
        )}
        <a class="btn btn-ghost" href="#/">
          {t.join.backToLogin}
        </a>
      </form>
      <Footer />
    </div>
  );
}
